import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/** Visual treatment. `solid` is a filled button, `tertiary` is text-only. */
export type BzButtonVariant = 'solid' | 'outline' | 'tertiary';

/** Semantic colour. Maps onto the --bz-color-* semantic tokens. */
export type BzButtonTone = 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger';

/** `fit` drops the fixed control height and shrinks to its content. */
export type BzButtonSize = 'sm' | 'md' | 'fit';

/**
 * The reference component of the Bringoz design system.
 *
 * Every other bz-* component is expected to follow the shape used here:
 * standalone, signal inputs and outputs, OnPush, no constructor injection of
 * host-application services, and all visual values taken from tokens.
 *
 * Replaces `bringoz-button` from the legacy console. Input names changed
 * where the legacy ones were ambiguous — see the mapping table in
 * projects/ui/README.md before migrating a template.
 */
@Component({
  selector: 'bz-button',
  templateUrl: './bz-button.html',
  styleUrl: './bz-button.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzButton {
  readonly variant = input<BzButtonVariant>('solid');
  readonly tone = input<BzButtonTone>('primary');
  readonly size = input<BzButtonSize>('md');
  readonly disabled = input(false);

  /** Native button type. Named to match the DOM attribute, not the variant. */
  readonly type = input<'button' | 'submit' | 'reset'>('button');

  /**
   * Accessible name for a button whose content does not read as one — an
   * icon-only button, which is what `size="fit"` around a `bz-icon` is for.
   *
   * This was missing until 2026-09-17, and the consequence was not
   * hypothetical: `bz-dialog-layout` had been passing `ariaLabel="Close
   * dialog"` to a component with no such input since it was written, so
   * Angular left it as an inert HTML attribute on the host and the close ×
   * on **every dialog in the library** announced as "button". See
   * docs/accessibility.md, including why the audit never caught it.
   *
   * `bz-icon`'s own `label` is the alternative when the icon *is* the whole
   * content and you would rather name it there.
   */
  readonly ariaLabel = input('');

  readonly clicked = output<MouseEvent>();

  protected readonly buttonClass = computed(
    () => `bz-button bz-button--${this.variant()} bz-button--${this.tone()} bz-button--${this.size()}`,
  );
}
